/**
 * Snippet Script Loader
 * 
 * Loads snippet-specific JavaScript based on data attributes.
 * Uses eager glob imports to avoid dynamic import issues with Shopify's CDN.
 */

// Eagerly import all snippet modules using Vite's glob import
const snippetModules = import.meta.glob('../snippets/*.js', { eager: true });

// Create a map of snippet name to module
const snippetMap = {};
for (const path in snippetModules) {
  // Extract snippet name from path (e.g., '../snippets/product-card.js' -> 'product-card')
  const name = path.match(/\/([^/]+)\.js$/)?.[1];
  if (name) {
    snippetMap[name] = snippetModules[path];
  }
}

/**
 * Load snippet-specific scripts
 */
export function loadSnippetScripts() {
  const snippets = document.querySelectorAll('[data-snippet-script]');
  
  snippets.forEach((snippet) => {
    const scriptName = snippet.dataset.snippetScript;
    
    if (scriptName) {
      loadSnippetScript(scriptName, snippet);
    }
  });
}

/**
 * Load a specific snippet script
 */
function loadSnippetScript(scriptName, element) {
  try {
    const module = snippetMap[scriptName];
    
    if (!module) {
      // Snippet script doesn't exist, silently skip
      return;
    }
    
    if (module.default) {
      // If the module exports a default class/function, instantiate it
      if (typeof module.default === 'function') {
        new module.default(element);
      }
    } else if (module.init) {
      // If the module exports an init function, call it
      module.init(element);
    }
    
    // console.log(`✨ Loaded snippet script: ${scriptName}`);
  } catch (error) {
    console.warn(`Failed to load snippet script: ${scriptName}`, error);
  }
}

/**
 * Observer for dynamically added snippets (for theme editor and dynamic content)
 */
export function observeSnippets() {
  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType === 1 && node.hasAttribute('data-snippet-script')) {
          const scriptName = node.dataset.snippetScript;
          loadSnippetScript(scriptName, node);
        }
        
        // Also check children of added nodes
        if (node.nodeType === 1) {
          const childSnippets = node.querySelectorAll('[data-snippet-script]');
          childSnippets.forEach((snippet) => {
            const scriptName = snippet.dataset.snippetScript;
            loadSnippetScript(scriptName, snippet);
          });
        }
      });
    });
  });

  observer.observe(document.body, {
    childList: true,
    subtree: true,
  });
}

// Initialize snippet observer
observeSnippets();

